import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';

const Navigation = ({ darkMode, toggleDarkMode }) => {
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  
  const isActive = (path) => {
    return location.pathname === path;
  };
  
  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/calculator', label: 'Calculator' },
    { path: '/fruit-planner', label: 'Fruit Planner' },
    { path: '/comparison', label: 'Comparison Tools' },
  ];

  return (
    <nav className={`sticky top-0 z-50 transition-theme duration-300 ease-in-out ${
      darkMode 
        ? 'bg-gradient-to-r from-gray-900 to-gray-800 text-white shadow-lg border-b border-gray-700' 
        : 'bg-gradient-to-r from-primary-light to-blue-500 text-white shadow-md'
    }`}>
      <div className="container mx-auto max-w-7xl">
        <div className="flex items-center justify-between px-4 py-3">
          <Link to="/" className="flex items-center space-x-3 group hover:opacity-90 transition-opacity">
            <div className="p-1.5 bg-white bg-opacity-20 rounded-full group-hover:bg-opacity-30 transition-all">
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 1L3 5V19L12 23L21 19V5L12 1Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 9C13.6569 9 15 7.65685 15 6C15 4.34315 13.6569 3 12 3C10.3431 3 9 4.34315 9 6C9 7.65685 10.3431 9 12 9Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                <path d="M12 9V23" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </div>
            <h1 className="text-2xl font-bold tracking-tight">Blox Fruits Calculator</h1>
          </Link>
          
          {/* Desktop navigation */}
          <div className="hidden md:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link 
                key={link.path}
                to={link.path} 
                className={`relative px-4 py-2 rounded-lg mx-1 transition-all duration-200 ${
                  isActive(link.path) 
                    ? `${darkMode ? 'bg-gray-700' : 'bg-blue-600'} text-white font-medium` 
                    : 'text-gray-100 hover:bg-white hover:bg-opacity-10'
                }`}
              >
                {link.label}
                {isActive(link.path) && (
                  <span className={`absolute bottom-0 left-1/2 transform -translate-x-1/2 w-1/2 h-0.5 ${
                    darkMode ? 'bg-secondary-dark' : 'bg-white'
                  }`}></span>
                )}
              </Link>
            ))}
            <button 
              onClick={toggleDarkMode} 
              aria-label="Toggle dark mode"
              className={`ml-2 p-2 rounded-full transition-colors duration-200 ${
                darkMode 
                  ? 'bg-gray-700 hover:bg-gray-600 text-yellow-300 hover:text-yellow-200' 
                  : 'bg-blue-600 hover:bg-blue-700 text-white hover:text-yellow-100'
              }`}
            >
              {darkMode ? (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                  <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                </svg>
              )}
            </button>
          </div>
          
          {/* Mobile menu button */}
          <div className="md:hidden">
            <button 
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`p-2 rounded-md transition-colors duration-200 ${
                isMobileMenuOpen 
                  ? (darkMode ? 'bg-gray-700' : 'bg-blue-600') 
                  : 'hover:bg-white hover:bg-opacity-10'
              }`}
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
        
        {/* Mobile navigation */}
        {isMobileMenuOpen && (
          <div className={`md:hidden px-4 py-2 pb-4 space-y-1 transition-all duration-300 ${
            darkMode ? 'bg-gray-800' : 'bg-primary-dark'
          }`}>
            {navLinks.map((link) => (
              <Link 
                key={link.path}
                to={link.path} 
                className={`block py-2 px-3 rounded-lg transition-colors duration-200 ${
                  isActive(link.path) 
                    ? `${darkMode ? 'bg-gray-700' : 'bg-blue-600'} text-white font-bold` 
                    : `${darkMode ? 'hover:bg-gray-700' : 'hover:bg-blue-600'} text-gray-200 hover:text-white`
                }`}
                onClick={() => setIsMobileMenuOpen(false)}
              >
                {link.label}
              </Link>
            ))}
            <button 
              onClick={(e) => {
                e.stopPropagation();
                toggleDarkMode();
              }} 
              className={`flex items-center w-full py-2 px-3 mt-2 rounded-lg space-x-2 transition-colors duration-200 ${
                darkMode ? 'bg-gray-700 hover:bg-gray-600' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {darkMode ? (
                <>
                  <svg className="w-5 h-5 text-yellow-300" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M10 2a1 1 0 011 1v1a1 1 0 11-2 0V3a1 1 0 011-1zm4 8a4 4 0 11-8 0 4 4 0 018 0zm-.464 4.95l.707.707a1 1 0 001.414-1.414l-.707-.707a1 1 0 00-1.414 1.414zm2.12-10.607a1 1 0 010 1.414l-.706.707a1 1 0 11-1.414-1.414l.707-.707a1 1 0 011.414 0zM17 11a1 1 0 100-2h-1a1 1 0 100 2h1zm-7 4a1 1 0 011 1v1a1 1 0 11-2 0v-1a1 1 0 011-1zM5.05 6.464A1 1 0 106.465 5.05l-.708-.707a1 1 0 00-1.414 1.414l.707.707zm1.414 8.486l-.707.707a1 1 0 01-1.414-1.414l.707-.707a1 1 0 011.414 1.414zM4 11a1 1 0 100-2H3a1 1 0 000 2h1z" clipRule="evenodd" />
                  </svg>
                  <span>Switch to Light Mode</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                    <path d="M17.293 13.293A8 8 0 016.707 2.707a8.001 8.001 0 1010.586 10.586z" />
                  </svg>
                  <span>Switch to Dark Mode</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </nav>
  );
};

export default Navigation;
